// Keyboard input. Uses event.code (physical keys) so it's layout-independent (plan §9-D).
// Movement is the Arrow keys (the letter keys are reserved for active abilities); ability
// presses are edge-tracked so each tap casts once. Exposes a reusable movement vector.

import { looksXbox, setInputMode } from './inputMode';

const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space']);
const UP = ['ArrowUp'];
const DOWN = ['ArrowDown'];
const LEFT = ['ArrowLeft'];
const RIGHT = ['ArrowRight'];
const PAD_DEADZONE = 0.25; // ignore Xbox analog-stick drift below this
const TOUCH_DEADZONE = 6;
const TOUCH_MAX_RADIUS = 52; // small thumb drag = full speed

export interface TouchJoystickVisual {
  start(x: number, y: number): void;
  move(dx: number, dy: number, maxRadius: number): void;
  end(): void;
}

export class Input {
  private readonly down = new Set<string>();
  /** Normalized movement direction, refreshed by sample(). Reused - do not retain. */
  readonly move = { x: 0, y: 0 };
  /** Ability hotkeys pressed since the last clear (edge-triggered). Reused - do not retain. */
  readonly abilityPresses: string[] = [];
  private readonly abilityKeys: Set<string>;
  private readonly abilityKeyList: readonly string[]; // ordered, for gamepad button → ability
  private readonly prevPadButtons: boolean[] = []; // edge-detect gamepad buttons
  private prevPadMenu = false; // edge-detect the gamepad Menu/Start button
  private readonly target: Window;
  private pauseQueued = false;
  // Floating-joystick touch state (mobile): first touch = anchor, drag sets the direction.
  private touchId: number | null = null;
  private anchorX = 0;
  private anchorY = 0;
  private touchVX = 0;
  private touchVY = 0;

  private touchEl: HTMLElement | null = null;
  private touchVisual: TouchJoystickVisual | null = null;

  constructor(target: Window = window, abilityKeys: readonly string[] = []) {
    this.target = target;
    this.abilityKeys = new Set(abilityKeys);
    this.abilityKeyList = abilityKeys;
    target.addEventListener('keydown', this.onKeyDown);
    target.addEventListener('keyup', this.onKeyUp);
    target.addEventListener('blur', this.onBlur);
  }

  /** Wire the touch joystick to the game canvas only, so taps on DOM UI (draft cards,
   *  menu, buttons) keep working - those sit above the canvas and never reach it. */
  attachTouch(el: HTMLElement, visual: TouchJoystickVisual | null = null): void {
    this.touchEl = el;
    this.touchVisual = visual;
    el.addEventListener('touchstart', this.onTouchStart, { passive: false });
    el.addEventListener('touchmove', this.onTouchMove, { passive: false });
    el.addEventListener('touchend', this.onTouchEnd);
    el.addEventListener('touchcancel', this.onTouchEnd);
  }

  dispose(): void {
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('blur', this.onBlur);
    const el = this.touchEl;
    if (el !== null) {
      el.removeEventListener('touchstart', this.onTouchStart);
      el.removeEventListener('touchmove', this.onTouchMove);
      el.removeEventListener('touchend', this.onTouchEnd);
      el.removeEventListener('touchcancel', this.onTouchEnd);
    }
  }

  /** Queue an ability cast programmatically (tapping the on-screen ability bar). */
  pressAbility(code: string): void {
    this.abilityPresses.push(code);
  }

  // --- touch joystick ---

  private onTouchStart = (e: TouchEvent): void => {
    if (this.touchId !== null) return; // already tracking a finger
    const t = e.changedTouches[0];
    if (t === undefined) return;
    // Ignore touches that start on UI (buttons/links/menu) so taps don't also steer.
    const el = e.target as HTMLElement | null;
    if (el !== null && el.closest('button, a, input, select') !== null) return;
    this.touchId = t.identifier;
    this.anchorX = t.clientX;
    this.anchorY = t.clientY;
    this.touchVX = 0;
    this.touchVY = 0;
    this.touchVisual?.start(this.anchorX, this.anchorY);
    this.touchVisual?.move(0, 0, TOUCH_MAX_RADIUS);
    e.preventDefault();
  };

  private onTouchMove = (e: TouchEvent): void => {
    if (this.touchId === null) return;
    let t: Touch | undefined;
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this.touchId) {
        t = e.changedTouches[i];
        break;
      }
    }
    if (t === undefined) return;
    const dx = t.clientX - this.anchorX;
    const dy = t.clientY - this.anchorY;
    const dist = Math.hypot(dx, dy);
    const visualClamp = dist > TOUCH_MAX_RADIUS ? TOUCH_MAX_RADIUS / dist : 1;
    this.touchVisual?.move(dx * visualClamp, dy * visualClamp, TOUCH_MAX_RADIUS);
    if (dist <= TOUCH_DEADZONE) {
      this.touchVX = 0;
      this.touchVY = 0;
    } else {
      const mag = Math.min((dist - TOUCH_DEADZONE) / (TOUCH_MAX_RADIUS - TOUCH_DEADZONE), 1);
      this.touchVX = (dx / dist) * mag;
      this.touchVY = (dy / dist) * mag;
    }
    e.preventDefault();
  };

  private onTouchEnd = (e: TouchEvent): void => {
    if (this.touchId === null) return;
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this.touchId) {
        this.touchId = null;
        this.touchVX = 0;
        this.touchVY = 0;
        this.touchVisual?.end();
        return;
      }
    }
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    setInputMode('keyboard');
    if (SCROLL_KEYS.has(e.code)) e.preventDefault();
    if (!this.down.has(e.code)) {
      if (e.code === 'KeyP' || e.code === 'Escape') this.pauseQueued = true;
      if (this.abilityKeys.has(e.code)) this.abilityPresses.push(e.code); // edge: once per tap
    }
    this.down.add(e.code);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.down.delete(e.code);
  };

  // Releasing all keys when the window loses focus avoids "stuck" movement.
  private onBlur = (): void => {
    this.down.clear();
    if (this.touchId !== null) {
      this.touchId = null;
      this.touchVX = 0;
      this.touchVY = 0;
      this.touchVisual?.end();
    }
  };

  private any(codes: readonly string[]): boolean {
    for (let i = 0; i < codes.length; i++) if (this.down.has(codes[i])) return true;
    return false;
  }

  /** Recompute the movement vector. Call once per sim tick. */
  sample(): void {
    // Gamepad first - it also edge-detects ability buttons. A finger overrides movement.
    const padMoved = this.gamepadMove();
    if (this.touchId !== null) {
      this.move.x = this.touchVX;
      this.move.y = this.touchVY;
      return;
    }
    if (padMoved) return; // gamepadMove() set this.move from the stick / d-pad
    let x = 0;
    let y = 0;
    if (this.any(UP)) y -= 1;
    if (this.any(DOWN)) y += 1;
    if (this.any(LEFT)) x -= 1;
    if (this.any(RIGHT)) x += 1;
    if (x !== 0 && y !== 0) {
      x *= Math.SQRT1_2;
      y *= Math.SQRT1_2;
    }
    this.move.x = x;
    this.move.y = y;
  }

  /** Poll the first connected gamepad (Xbox etc.): left stick / d-pad → movement; the face +
   *  shoulder buttons (A B X Y LB RB LT RT) → the 8 abilities, edge-triggered. Returns true
   *  when the stick/d-pad is driving movement (so it overrides the keyboard). */
  private gamepadMove(): boolean {
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    let gp: Gamepad | null = null;
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (p !== null) {
        gp = p;
        break;
      }
    }
    if (gp === null) return false;

    const btns = gp.buttons;
    let anyBtn = false;
    for (let i = 0; i < 8 && i < this.abilityKeyList.length; i++) {
      const pressed = btns[i]?.pressed ?? false;
      if (pressed) anyBtn = true;
      if (pressed && this.prevPadButtons[i] !== true) this.abilityPresses.push(this.abilityKeyList[i]);
      this.prevPadButtons[i] = pressed;
    }

    // Movement: left stick, overridden by the d-pad when held.
    let gx = gp.axes[0] ?? 0;
    let gy = gp.axes[1] ?? 0;
    if (btns[14]?.pressed === true) gx = -1;
    else if (btns[15]?.pressed === true) gx = 1;
    if (btns[12]?.pressed === true) gy = -1;
    else if (btns[13]?.pressed === true) gy = 1;
    const mag = Math.hypot(gx, gy);
    if (anyBtn || mag > PAD_DEADZONE) setInputMode('gamepad', looksXbox(gp.id)); // controller in use
    if (mag <= PAD_DEADZONE) return false;
    const m = Math.min(mag, 1); // analog speed, capped at 1
    this.move.x = (gx / mag) * m;
    this.move.y = (gy / mag) * m;
    return true;
  }

  /** Returns true once per pause keypress (edge-triggered). */
  consumePause(): boolean {
    const p = this.pauseQueued;
    this.pauseQueued = false;
    return p;
  }

  /** Edge-detect the gamepad Menu/Start button (index 9). Call once per render frame -
   *  it works even while paused/menu-open (unlike sample(), which the paused loop skips). */
  consumePadMenu(): boolean {
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    let gp: Gamepad | null = null;
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (p !== null) {
        gp = p;
        break;
      }
    }
    if (gp === null) {
      this.prevPadMenu = false;
      return false;
    }
    const pressed = gp.buttons[9]?.pressed ?? false;
    const edge = pressed && !this.prevPadMenu;
    this.prevPadMenu = pressed;
    return edge;
  }

  /** Clear the ability-press queue (call after the sim has consumed it for the tick). */
  clearAbilityPresses(): void {
    this.abilityPresses.length = 0;
  }
}
